import { useState } from "react";
import { toast } from "sonner";
import { Ticket, CheckCircle, X } from "@phosphor-icons/react";
import api, { errDetail } from "@/lib/api";
import { t } from "@/lib/i18n";

export const PromoCodeInput = ({ packageId = null, kind = null, onApplied, testId = "promo-code" }) => {
  const [val, setVal] = useState("");
  const [applied, setApplied] = useState(null);
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    const code = val.trim().toUpperCase();
    if (!code || busy) return;
    setBusy(true);
    try {
      const body = { code };
      if (packageId) body.package_id = packageId;
      if (kind) body.kind = kind;
      const { data } = await api.post("/billing/code/validate", body);
      setApplied(data);
      onApplied(code, data);
      toast.success(data.free
        ? t("promo.free_ok", "Code accepted — 100% off, no card needed")
        : t("promo.pct_ok", "Code accepted — {pct}% off", { pct: data.discount_pct }));
    } catch (e) { toast.error(errDetail(e)); }
    finally { setBusy(false); }
  };

  const clear = () => { setApplied(null); setVal(""); onApplied(null, null); };

  if (applied) {
    return (
      <div data-testid={`${testId}-applied`} className="inline-flex items-center gap-2 text-xs font-semibold bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-md px-3 py-1.5">
        <CheckCircle size={14} /> {applied.code} — {applied.free ? t("promo.free_label", "100% off (no card needed)") : `-${applied.discount_pct}%`}
        <span className="text-emerald-600 font-normal">{t("promo.uses_left", "{n} use(s) left", { n: applied.remaining_uses })} · {t("promo.until", "until")} {applied.valid_until}</span>
        <button data-testid={`${testId}-clear`} onClick={clear} className="text-emerald-500 hover:text-red-600"><X size={13} /></button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <Ticket size={15} className="text-gray-400" />
      <input data-testid={`${testId}-input`} value={val} onChange={(e) => setVal(e.target.value.toUpperCase())}
        onKeyDown={(e) => { if (e.key === "Enter") apply(); }}
        placeholder={t("promo.ph", "Have a code?")}
        className="border border-gray-300 rounded-md px-3 py-1.5 text-xs w-40 focus:outline-none focus:ring-2 focus:ring-blue-500" />
      <button data-testid={`${testId}-apply`} onClick={apply} disabled={busy || !val.trim()}
        className="text-xs font-semibold border border-gray-300 hover:bg-gray-50 rounded-md px-3 py-1.5 disabled:opacity-40">
        {t("promo.apply", "Apply")}
      </button>
    </div>
  );
};
